/**
 * TanStack Query hooks for the admin analytics dashboard (#24).
 *
 * Mirrors `api/src/analytics-admin/analytics-admin.routes.ts`:
 *
 *   - GET /api/admin/analytics?siteId=...&from=YYYY-MM-DD&to=YYYY-MM-DD
 *
 * The endpoint is read-only; no mutations. The hook is gated by the
 * `enabled` flag so the page can render the site selector before the
 * fetch fires (operators land on the page, the hook waits until they
 * pick a site, then fetches).
 *
 * `staleTime` is 60s — same as the API's in-process cache. A noisy
 * operator clicking around the date picker re-uses the cached response
 * within that window without an extra round-trip.
 */

import { useQuery } from "@tanstack/react-query";
import type { AdminAnalyticsResponse } from "@ottopilot/shared";
import { apiGet } from "../lib/api";
import type { ApiClientError } from "../lib/api";
import { queryKeys } from "./queryKeys";

export type UseAnalyticsDashboardArgs = {
  siteId: string | undefined;
  from: string;
  to: string;
};

const DASHBOARD_STALE_MS = 60_000;

export function useAnalyticsDashboard(args: UseAnalyticsDashboardArgs) {
  // The query is enabled only when a siteId is selected. We pass empty
  // strings to the queryKey factory when disabled to keep the key shape
  // stable; TanStack Query won't fire because of the `enabled` flag.
  const params = {
    siteId: args.siteId ?? "",
    from: args.from,
    to: args.to,
  };
  return useQuery<AdminAnalyticsResponse, ApiClientError>({
    queryKey: queryKeys.analytics.dashboard(params),
    queryFn: () =>
      apiGet<AdminAnalyticsResponse>("/api/admin/analytics", {
        query: params,
      }),
    enabled: Boolean(args.siteId),
    staleTime: DASHBOARD_STALE_MS,
  });
}
